// Authentication Hook - Sicher: Rolle wird erst nach Profil-Load gesetzt

import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import type { User, Customer, UserRole } from '@/lib/types';
import { useEffect } from 'react';

interface UserWithCustomer extends User {
  customer?: Customer | null;
}

interface AuthState {
  user: UserWithCustomer | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

let stopAuthInit = false;

/**
 * Entfernt veraltete Supabase-Session-Token aus dem Browser-Speicher.
 * Genau das, was bisher das manuelle "Cookies löschen" gemacht hat – behebt
 * den Deadlock, bei dem supabase-js beim Refresh eines abgelaufenen Tokens am
 * internen Auth-Lock hängt und der Login "ewig lädt".
 */
function clearAuthStorage() {
  try {
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith('sb-') && k.includes('auth-token')) localStorage.removeItem(k);
    });
  } catch {
    /* ignore */
  }
}

function isTimeout(e: any): boolean {
  return typeof e?.message === 'string' && e.message.startsWith('__timeout__');
}

/** Promise mit hartem Timeout – verhindert "endloses Laden". */
function withTimeout<T>(p: PromiseLike<T>, ms: number): Promise<T> {
  return Promise.race([
    Promise.resolve(p),
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('__timeout__')), ms)),
  ]);
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  error: null,

  signIn: async (email: string, password: string) => {
    stopAuthInit = true;
    set({ loading: true, error: null });

    try {
      // Schritt 1: Login – mit Timeout gegen den "endlos ladenden" Deadlock
      // (veralteter Token → supabase-js hängt am Auth-Lock). Bei Timeout leeren
      // wir den Auth-Speicher, sodass der nächste Klick sofort klappt.
      let data: any, error: any;
      try {
        ({ data, error } = await withTimeout(
          supabase.auth.signInWithPassword({ email, password }),
          10000
        ));
      } catch (e: any) {
        if (isTimeout(e)) {
          clearAuthStorage();
          set({ loading: false, error: 'Anmeldung hat zu lange gedauert – bitte gleich erneut auf „Anmelden" klicken.' });
          return;
        }
        throw e;
      }

      if (error) throw error;
      if (!data?.user) {
        set({ loading: false });
        return;
      }

      // Schritt 2: Profil laden. OHNE Profil gibt es KEINEN Zugang — fail
      // closed. Der frühere Weiterlauf mit geratener Rolle und leerer
      // Kundenzuordnung führte am 04.08.2026 zum Datenleck: Die
      // Submissions-Seite lud dann ohne Kundenfilter. Lieber ein klarer
      // Fehler mit erneutem Login als ein halb eingeloggter Zustand.
      let profile: { role?: string; customer_id?: string | null } | null = null;
      try {
        const r = await withTimeout(
          supabase.from('user_profiles').select('role, customer_id').eq('id', data.user.id).single(),
          8000
        );
        profile = (r as any).data;
      } catch {
        /* wird unten als fehlendes Profil behandelt */
      }
      if (!profile) {
        await supabase.auth.signOut().catch(() => {});
        clearAuthStorage();
        set({
          user: null,
          loading: false,
          error: 'Ihr Benutzerprofil konnte nicht geladen werden – bitte melden Sie sich erneut an.',
        });
        return;
      }

      // Schritt 3: Customer laden (falls vorhanden)
      let customerData = null;
      if (profile?.customer_id) {
        try {
          const r = await withTimeout(
            supabase.from('customers').select('*').eq('id', profile.customer_id).single(),
            8000
          );
          customerData = (r as any).data;
        } catch {
          /* ignore */
        }
      }

      // Schritt 4: State setzen
      set({
        user: {
          id: data.user.id,
          email: data.user.email!,
          created_at: data.user.created_at,
          // Rolle 1:1 aus user_profiles (admin | customer | vertrieb, Migration 073)
          role: (profile?.role || 'customer') as UserRole,
          customer_id: profile?.customer_id || null,
          customer: customerData,
        },
        loading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('[SignIn] error:', error);
      set({ error: error.message || 'Anmeldung fehlgeschlagen', loading: false });
      throw error;
    }
  },

  signOut: async () => {
    set({ user: null, loading: false, error: null });
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  },

  resetPassword: async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
    } catch (error: any) {
      console.error('Password reset error:', error);
      throw error;
    }
  },
}));

/**
 * Profil laden und User-State setzen
 */
async function loadUserProfile(authUser: { id: string; email?: string; created_at: string }) {
  const { data: profile, error: profileError } = await supabase
    .from('user_profiles')
    .select('role, customer_id, display_name')
    .eq('id', authUser.id)
    .single();

  // Fail closed: Ohne Profil keine Sitzung (siehe Kommentar in signIn —
  // ein halb eingeloggter Nutzer war die Ursache des Datenlecks am 04.08.2026).
  if (profileError || !profile) {
    console.error('[Auth] Profile load failed:', profileError);
    await supabase.auth.signOut().catch(() => {});
    useAuthStore.setState({
      user: null,
      loading: false,
      error: 'Sitzung beendet – Ihr Benutzerprofil konnte nicht geladen werden. Bitte melden Sie sich erneut an.',
    });
    return null;
  }

  let customerData = null;
  if (profile?.customer_id) {
    const { data: customer } = await supabase
      .from('customers')
      .select('*')
      .eq('id', profile.customer_id)
      .single();
    customerData = customer;
  }

  const userRole = profile?.role || 'customer';

  useAuthStore.setState({
    user: {
      id: authUser.id,
      email: authUser.email!,
      created_at: authUser.created_at,
      role: userRole as UserRole,
      customer_id: profile?.customer_id || null,
      customer: customerData,
    },
    loading: false,
    error: null,
  });

  return userRole;
}

/**
 * Hook für Auth Initialization
 */
export function useAuthInit() {
  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        // getSession mit Timeout: hängt der Refresh eines veralteten Tokens,
        // verwerfen wir ihn (verhindert den Login-Deadlock beim nächsten Klick).
        const { data: { session }, error } = await withTimeout(supabase.auth.getSession(), 8000);

        if (error || !session?.user) {
          if (mounted) {
            useAuthStore.setState({ user: null, loading: false });
          }
          return;
        }

        if (mounted && !stopAuthInit) {
          await loadUserProfile(session.user);
        } else if (mounted) {
          useAuthStore.setState({ user: null, loading: false });
        }
      } catch (error) {
        // Timeout/Deadlock → veralteten Token leeren, Login-Screen zeigen
        if (isTimeout(error)) clearAuthStorage();
        console.error('[Auth] Init error:', error);
        if (mounted) {
          useAuthStore.setState({ user: null, loading: false });
        }
      }
    };

    initAuth();

    // Sicherheits-Timeout: Falls die Initialisierung hängt (z.B. veralteter
    // Session-Token nach einem Deploy), nicht ewig im Ladezustand bleiben,
    // sondern den Login-Screen zeigen. Verhindert das "endlose Laden".
    const safetyTimer = setTimeout(() => {
      if (!mounted) return;
      const state = useAuthStore.getState();
      if (state.loading) {
        console.warn('[Auth] Init-Timeout erreicht – erzwinge Login-Screen');
        useAuthStore.setState({ user: null, loading: false });
      }
    }, 8000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[Auth] Event:', event);

        if (!mounted) return;

        if (event === 'SIGNED_OUT') {
          useAuthStore.setState({ user: null, loading: false });
          return;
        }

        if (event === 'PASSWORD_RECOVERY') {
          console.log('[Auth] Password recovery event - redirecting to reset page');
          useAuthStore.setState({ loading: false });
          window.location.href = '/reset-password';
          return;
        }

        if (event === 'SIGNED_IN' && session?.user && !stopAuthInit) {
          // WICHTIG: KEIN `await supabase.*` direkt in diesem Callback!
          // onAuthStateChange läuft, während supabase-js den Auth-Lock HÄLT.
          // loadUserProfile() ruft supabase.from(...) auf → will denselben Lock
          // → DEADLOCK. Folge: ab dem ersten erneuten SIGNED_IN (Tab-Fokus/
          // Token-Refresh) hängt JEDER weitere DB-Aufruf ("Speichern lädt ewig",
          // genau eine Aktion pro Reload). Deshalb die Arbeit AUS dem Callback
          // herauslösen — setTimeout(0) läuft erst, nachdem der Lock frei ist.
          const u = session.user;
          setTimeout(() => { if (mounted) void loadUserProfile(u); }, 0);
        }
      }
    );

    return () => {
      mounted = false;
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
    };
  }, []);
}
